import { isJust, nothing, type Maybe, just } from "./maybe";
import { displayFileName } from "./fileNames";
import {
  chapterFolderFromScenePath,
  isDefaultSceneFileName,
  PROJECT_CHAPTERS_DIR,
  PROJECT_DATABASE_DIR,
  PROJECT_NOTES_DIR,
  PROJECT_TEMPLATES_DIR,
} from "./zwConfig";

export type TrashKind =
  "chapter" | "scene" | "template" | "note" | "database" | "unplaced";

/** Classify a project-relative deleted path for the Trash tab. A draft
 * scene/chapter (moved out of the manuscript into Notes/) lives inside
 * `Notes/` too, one or two levels deep — distinguished from a plain note
 * purely by path depth here, since this module has no way to read a file's
 * frontmatter `kind` marker; a one-level-deep `Notes/<file>.md` is always a
 * plain note or a draft scene (both shown the same, as "Draft" is a
 * generalization the UI doesn't need to split further for Trash), while
 * anything nested another level under `Notes/` can only be a draft
 * chapter's own file. */
export function trashKind(relativePath: string): Maybe<TrashKind> {
  const path = relativePath.replace(/\\/g, "/");
  if (path.length === 0 || path.includes("..")) return nothing;
  if (path.includes("/") === false) return just("chapter");
  if (path.startsWith(`${PROJECT_TEMPLATES_DIR}/`)) return just("template");
  if (path.startsWith(`${PROJECT_NOTES_DIR}/`)) {
    const rest = path.slice(PROJECT_NOTES_DIR.length + 1);
    return rest.includes("/") ? just("unplaced") : just("note");
  }
  if (path.startsWith(`${PROJECT_DATABASE_DIR}/`)) return just("database");
  if (path.startsWith(`${PROJECT_CHAPTERS_DIR}/`)) {
    return /\/index\.(zwdoc|md)$/i.test(path) ? just("chapter") : just("scene");
  }
  return nothing;
}

export function trashKindLabel(kind: TrashKind): string {
  if (kind === "chapter") return "Chapter";
  if (kind === "scene") return "Scene";
  if (kind === "template") return "Template";
  if (kind === "note") return "Note";
  if (kind === "unplaced") return "Draft";
  return "Worldbuilding";
}

/**
 * Writer-facing name without directory or extension. A deleted scene still using its
 * auto-generated `sceneN.md` name has nothing meaningful of its own to show (an ordinal
 * position isn't stable once other scenes are added/removed) — show the chapter it
 * came from instead. A custom-renamed scene shows its own name, same as a chapter.
 */
export function trashLabel(relativePath: string): string {
  const path = relativePath.replace(/\\/g, "/");
  const kindM = trashKind(path);
  const basename = path.split("/").pop() ?? path;
  const isDefaultOrIndex =
    (kindM.tag === "just" && kindM.value === "chapter") ||
    (kindM.tag === "just" &&
      kindM.value === "scene" &&
      isDefaultSceneFileName(basename));
  if (isDefaultOrIndex) {
    const folderM = chapterFolderFromScenePath(path);
    if (isJust(folderM) && folderM.value.length > 0) return folderM.value;
  }
  return displayFileName(relativePath);
}

/**
 * Parent chapter name for the meta line — only when it isn't already the title
 * (a still-default-named scene's title *is* the chapter name via `trashLabel` above,
 * so repeating it here would be redundant; a custom-named scene's title is its own
 * name, so this is the only place the chapter context shows up).
 */
export function trashChapterContext(relativePath: string): Maybe<string> {
  const path = relativePath.replace(/\\/g, "/");
  const basename = path.split("/").pop() ?? path;
  const kindM = trashKind(path);
  if (
    kindM.tag !== "just" ||
    kindM.value !== "scene" ||
    isDefaultSceneFileName(basename)
  ) {
    return nothing;
  }
  return chapterFolderFromScenePath(path);
}
